
import { defineComponent } from "vue";
import store from "@/store";
import SaberWeightsService from "@/api/AcademicBase/SaberWeightsService";
import AcademicYearService from "@/api/Configuration/AcademicYearService";
import SaberTypesService from "@/api/AcademicBase/SaberTypesService";
import type {
  ISaberWeightsModel,
  IAcademicYearModel,
  ISaberTypesModel,
} from "@/interfaces/modules";

export default defineComponent({
  name: "AcademicYearSaberWeightsContent",
  data() {
    return {
      form: {
        dialog: false,
        record: {} as ISaberWeightsModel,
      },
      table: {
        filters: { search: "" },
        currentPage: 1,
        loading: false,
        headers: [
          { key: "AcademicYearName", label: "Año Académico", sortable: true },
          { key: "SaberTypeName",   label: "Tipo de Saber",  sortable: true },
          { key: "WeightPercent",   label: "Valor académico (%)",       sortable: true },
          { key: "CreatedByFormat", label: "Creación",       sortable: true },
          { key: "ModifiedByFormat",label: "Última modificación", sortable: true },
          { key: "action",          label: "Acciones",       sortable: false },
        ],
        records: [] as ISaberWeightsModel[],
        rowsPerPage: 10,
      },
      academicYears: [] as IAcademicYearModel[],
      saberTypes: [] as ISaberTypesModel[],
      saving: false,
    };
  },
  computed: {
    filteredItems(): ISaberWeightsModel[] {
      const q = (this.table.filters.search ?? "").toLowerCase().trim();
      if (!q) return this.table.records;

      return this.table.records.filter((x) => {
        const year = this.getAcademicYearName(x.AcademicYearId).toLowerCase();
        const saber = this.getSaberTypeName(x.SaberTypeId).toLowerCase();
        const weight = (x.WeightPercent ?? 0).toString();
        return year.includes(q) || saber.includes(q) || weight.includes(q);
      });
    },
    rows(): number {
      return this.table.records.length;
    },
  },
  async mounted() {
    await this.load();
  },
  methods: {
    async load() {
      this.table.loading = true;
      try {
        // Cargar catálogos en paralelo
        const [years, sabers] = await Promise.all([
          AcademicYearService.GetAcademicYears(),
          SaberTypesService.GetSaberTypes(),
        ]);
        this.academicYears = years ?? [];
        this.saberTypes = sabers ?? [];

        // Cargar registros
        this.table.records = await SaberWeightsService.GetAcademicYearSaberWeights();
      } finally {
        this.table.loading = false;
      }
    },

    academicYearText(y: IAcademicYearModel) {
      // Ajusta según tus campos reales, ejemplo: y.Name o y.Year
      return y.Name ?? String(y.Id);
    },
    getAcademicYearName(id?: number) {
      const y = this.academicYears.find((r) => r.Id === id);
      return y ? this.academicYearText(y) : `#${id ?? "-"}`;
    },
    getSaberTypeName(id?: number) {
      const s = this.saberTypes.find((r) => r.Id === id);
      return s ? s.Name : `#${id ?? "-"}`;
    },
    formatPercent(v?: number) {
      if (v === null || v === undefined) return "-";
      return `${Number(v).toFixed(2)}%`;
    },

    async edit(item: ISaberWeightsModel) {
      this.form.record = await SaberWeightsService.GetAcademicYearSaberWeightsById(item);
      this.form.dialog = true;
    },

    openModal() {
      this.form.record = { Id: 0, WeightPercent: 0 } as ISaberWeightsModel;
      this.form.dialog = true;
    },

    async handleSubmit() {
      // Validación mínima
      if (!this.form.record.AcademicYearId || !this.form.record.SaberTypeId) {
        store.dispatch("core/popupDialog", {
          text: "Seleccione Año Académico y Tipo de Saber.",
          type: "warning",
          showCancelButton: false,
        });
        return;
      }
      if (this.form.record.WeightPercent === undefined || this.form.record.WeightPercent === null) {
        store.dispatch("core/popupDialog", {
          text: "Ingrese el porcentaje de peso.",
          type: "warning",
          showCancelButton: false,
        });
        return;
      }

      try {
        this.form.dialog = false;
        await SaberWeightsService.PostAcademicYearSaberWeights(this.form.record);

        store.dispatch("core/popupDialog", {
          text: "Registro guardado con éxito.",
          type: "success",
          showCancelButton: false,
        });

        await this.load();
      } finally {
        this.saving = false;
      }
    },
  },
});
